/**
 * SubscriptionGateErrorBoundary — catches any crash inside SubscriptionGate
 * and renders children (dashboard) instead of a white screen.
 * 
 * Philosophy: dashboard access > white screen. Always.
 */
import { Component, ReactNode } from "react";
import { captureError } from "@/lib/observability";

interface Props {
  children: ReactNode;
  fallback: ReactNode;
}

interface State {
  hasError: boolean;
}

export class SubscriptionGateErrorBoundary extends Component<Props, State> {
  /** Report once per mount — a crashing subtree must never create an error storm. */
  private reported = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (this.reported) return;
    this.reported = true;
    console.error("[SubscriptionGateErrorBoundary] Caught crash — rendering fallback (dashboard):", error.message);
    console.error("[SubscriptionGateErrorBoundary] Component stack:", info.componentStack);
    captureError("react.subscription_gate", error, {
      metadata: { componentStack: info.componentStack?.slice(0, 300) },
    });
  }

  render() {
    if (this.state.hasError) {
      return <>{this.props.fallback}</>;
    }
    return this.props.children;
  }
}
